import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useGuild } from '../contexts/GuildContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    DialogFooter,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
    Ticket, 
    Plus, 
    Search, 
    Filter,
    ChevronLeft,
    ChevronRight,
    Clock,
    User,
    Tag
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Tickets() {
    const { token } = useAuth();
    const { selectedGuild } = useGuild();
    const navigate = useNavigate();
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [filters, setFilters] = useState({
        status: '',
        priority: '',
        category: '',
        search: ''
    });
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [newTicket, setNewTicket] = useState({
        subject: '',
        category: 'support',
        priority: 'medium',
        initial_message: ''
    });
    const [creating, setCreating] = useState(false);

    const fetchTickets = async () => {
        if (!selectedGuild || !token) return;
        
        setLoading(true);
        try {
            const params = new URLSearchParams({
                guild_id: selectedGuild,
                page: page.toString(),
                limit: '10'
            });
            
            if (filters.status) params.append('status', filters.status);
            if (filters.priority) params.append('priority', filters.priority);
            if (filters.category) params.append('category', filters.category);

            const response = await axios.get(`${API}/tickets?${params}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            setTickets(response.data.tickets || []);
            setTotalPages(response.data.pages || 1);
        } catch (error) {
            console.error('Error fetching tickets:', error);
            toast.error('Failed to load tickets');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTickets();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedGuild, token, page, filters.status, filters.priority, filters.category]);

    const createTicket = async () => {
        if (!newTicket.subject.trim()) {
            toast.error('Subject is required');
            return;
        }

        setCreating(true);
        try {
            await axios.post(`${API}/tickets`, {
                guild_id: selectedGuild,
                ...newTicket
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            toast.success('Ticket created successfully');
            setCreateDialogOpen(false);
            setNewTicket({
                subject: '',
                category: 'support',
                priority: 'medium',
                initial_message: ''
            });
            fetchTickets();
        } catch (error) {
            console.error('Error creating ticket:', error);
            toast.error('Failed to create ticket');
        } finally {
            setCreating(false);
        }
    };

    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'high': return 'bg-red-500/20 text-red-500 border-red-500/30';
            case 'medium': return 'bg-amber-500/20 text-amber-500 border-amber-500/30';
            case 'low': return 'bg-green-500/20 text-green-500 border-green-500/30';
            default: return '';
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'open': return 'bg-green-500/20 text-green-500 border-green-500/30';
            case 'pending': return 'bg-amber-500/20 text-amber-500 border-amber-500/30';
            case 'closed': return 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30';
            default: return '';
        }
    };

    const getCategoryIcon = (category) => {
        switch (category) {
            case 'support': return '🎫';
            case 'sales': return '💰';
            case 'bug': return '🐛';
            default: return '📝';
        }
    };

    const filteredTickets = tickets.filter(ticket => 
        filters.search 
            ? ticket.subject.toLowerCase().includes(filters.search.toLowerCase()) ||
              ticket.username.toLowerCase().includes(filters.search.toLowerCase())
            : true
    );

    if (loading && tickets.length === 0) {
        return (
            <div className="space-y-4" data-testid="tickets-loading">
                <div className="flex justify-between items-center">
                    <Skeleton className="h-10 w-48" />
                    <Skeleton className="h-10 w-32" />
                </div>
                {[1, 2, 3, 4, 5].map(i => (
                    <Skeleton key={i} className="h-24" />
                ))}
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="tickets-page">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold">Tickets</h1>
                    <p className="text-muted-foreground">Manage and respond to support tickets</p>
                </div>
                
                <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
                    <DialogTrigger asChild>
                        <Button className="flex items-center gap-2" data-testid="create-ticket-btn">
                            <Plus className="w-4 h-4" />
                            New Ticket
                        </Button>
                    </DialogTrigger>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Create New Ticket</DialogTitle>
                            <DialogDescription>
                                Create a test ticket for this server
                            </DialogDescription>
                        </DialogHeader>
                        
                        <div className="space-y-4 py-4">
                            <div className="space-y-2">
                                <Label htmlFor="subject">Subject</Label>
                                <Input
                                    id="subject"
                                    placeholder="Brief description of the issue"
                                    value={newTicket.subject}
                                    onChange={e => setNewTicket({ ...newTicket, subject: e.target.value })}
                                    data-testid="ticket-subject-input"
                                />
                            </div>
                            
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Category</Label>
                                    <Select 
                                        value={newTicket.category}
                                        onValueChange={value => setNewTicket({ ...newTicket, category: value })}
                                    >
                                        <SelectTrigger data-testid="ticket-category-select">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="support">Support</SelectItem>
                                            <SelectItem value="sales">Sales</SelectItem>
                                            <SelectItem value="bug">Bug Report</SelectItem>
                                            <SelectItem value="other">Other</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                
                                <div className="space-y-2">
                                    <Label>Priority</Label>
                                    <Select 
                                        value={newTicket.priority}
                                        onValueChange={value => setNewTicket({ ...newTicket, priority: value })}
                                    >
                                        <SelectTrigger data-testid="ticket-priority-select">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="high">High</SelectItem>
                                            <SelectItem value="medium">Medium</SelectItem>
                                            <SelectItem value="low">Low</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            
                            <div className="space-y-2">
                                <Label htmlFor="message">Initial Message (Optional)</Label>
                                <Textarea
                                    id="message"
                                    placeholder="Describe your issue in detail..."
                                    value={newTicket.initial_message}
                                    onChange={e => setNewTicket({ ...newTicket, initial_message: e.target.value })}
                                    rows={4}
                                    data-testid="ticket-message-input"
                                />
                            </div>
                        </div>
                        
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
                                Cancel
                            </Button>
                            <Button onClick={createTicket} disabled={creating} data-testid="submit-ticket-btn">
                                {creating ? 'Creating...' : 'Create Ticket'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>

            {/* Filters */}
            <Card>
                <CardContent className="p-4">
                    <div className="flex flex-col md:flex-row gap-4">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                                placeholder="Search tickets..."
                                className="pl-10"
                                value={filters.search}
                                onChange={e => setFilters({ ...filters, search: e.target.value })}
                                data-testid="search-tickets-input"
                            />
                        </div>
                        
                        <Select 
                            value={filters.status}
                            onValueChange={value => setFilters({ ...filters, status: value })}
                        >
                            <SelectTrigger className="w-full md:w-40" data-testid="filter-status">
                                <SelectValue placeholder="Status" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="">All Status</SelectItem>
                                <SelectItem value="open">Open</SelectItem>
                                <SelectItem value="pending">Pending</SelectItem>
                                <SelectItem value="closed">Closed</SelectItem>
                            </SelectContent>
                        </Select>
                        
                        <Select 
                            value={filters.priority}
                            onValueChange={value => setFilters({ ...filters, priority: value })}
                        >
                            <SelectTrigger className="w-full md:w-40" data-testid="filter-priority">
                                <SelectValue placeholder="Priority" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="">All Priority</SelectItem>
                                <SelectItem value="high">High</SelectItem>
                                <SelectItem value="medium">Medium</SelectItem>
                                <SelectItem value="low">Low</SelectItem>
                            </SelectContent>
                        </Select>
                        
                        <Select 
                            value={filters.category}
                            onValueChange={value => setFilters({ ...filters, category: value })}
                        >
                            <SelectTrigger className="w-full md:w-40" data-testid="filter-category">
                                <SelectValue placeholder="Category" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="">All Categories</SelectItem>
                                <SelectItem value="support">Support</SelectItem>
                                <SelectItem value="sales">Sales</SelectItem>
                                <SelectItem value="bug">Bug Report</SelectItem>
                                <SelectItem value="other">Other</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </CardContent>
            </Card>

            {/* Tickets List */}
            <div className="space-y-3">
                {filteredTickets.length > 0 ? (
                    filteredTickets.map((ticket, index) => (
                        <motion.div
                            key={ticket.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.05 }}
                        >
                            <Card 
                                className="card-interactive cursor-pointer"
                                onClick={() => navigate(`/dashboard/tickets/${ticket.id}`)}
                                data-testid={`ticket-item-${ticket.id}`}
                            >
                                <CardContent className="p-4">
                                    <div className="flex items-start justify-between gap-4">
                                        <div className="flex items-start gap-4 flex-1">
                                            <div className={`w-1 h-full min-h-[60px] rounded-full ${ticket.priority === 'high' ? 'bg-red-500' : ticket.priority === 'medium' ? 'bg-amber-500' : 'bg-green-500'}`} />
                                            
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className="text-lg">{getCategoryIcon(ticket.category)}</span>
                                                    <h3 className="font-semibold truncate">{ticket.subject}</h3>
                                                </div>
                                                
                                                <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                                                    <span className="flex items-center gap-1">
                                                        <User className="w-3 h-3" />
                                                        {ticket.username}
                                                    </span>
                                                    <span className="flex items-center gap-1">
                                                        <Clock className="w-3 h-3" />
                                                        {new Date(ticket.created_at).toLocaleDateString()}
                                                    </span>
                                                    <span className="flex items-center gap-1">
                                                        <Tag className="w-3 h-3" />
                                                        {ticket.category}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <div className="flex items-center gap-2 flex-shrink-0">
                                            <Badge variant="outline" className={getPriorityColor(ticket.priority)}>
                                                {ticket.priority}
                                            </Badge>
                                            <Badge variant="outline" className={getStatusColor(ticket.status)}>
                                                {ticket.status}
                                            </Badge>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </motion.div>
                    ))
                ) : (
                    <Card>
                        <CardContent className="py-12 text-center">
                            <Ticket className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                            <h3 className="text-lg font-semibold mb-1">No tickets found</h3>
                            <p className="text-muted-foreground">
                                {filters.search || filters.status || filters.priority || filters.category
                                    ? 'Try adjusting your filters'
                                    : 'Tickets will appear here when created'}
                            </p>
                        </CardContent>
                    </Card>
                )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                        data-testid="prev-page-btn"
                    >
                        <ChevronLeft className="w-4 h-4" />
                    </Button>
                    
                    <span className="text-sm text-muted-foreground">
                        Page {page} of {totalPages}
                    </span>
                    
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                        data-testid="next-page-btn"
                    >
                        <ChevronRight className="w-4 h-4" />
                    </Button>
                </div>
            )}
        </div>
    );
}
